import { useEffect, useMemo, useRef, useState } from 'react'
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi'
import { useAuth } from '@astro/auth'
import { appUrl } from '@astro/config'
import { isAstrologerUser, useAstrologers, useAstrologerBookings } from '../useDashboard'

/* ===================== CONSULTATIONS ===================== */

/* Sub-tabs splitting the bookings by their status type. Buckets mirror
   statusStyle in useDashboard: cancel* → cancelled, complete/done →
   completed, everything else (confirmed/pending/…) counts as upcoming. */
const STATUS_TABS = [
  { key: 'all', label: 'All' },
  { key: 'upcoming', label: 'Upcoming' },
  { key: 'completed', label: 'Completed' },
  { key: 'cancelled', label: 'Cancelled' },
]

function statusType(b) {
  const s = String(b.status || '').toLowerCase()
  if (s.includes('cancel')) return 'cancelled'
  if (s.includes('complete') || s.includes('done')) return 'completed'
  return 'upcoming'
}

function AstrologerSkeletonCard() {
  return (
    <div className="browse-card p-4 flex flex-col animate-pulse">
      <div className="flex items-center justify-between mb-3">
        <div className="w-12 h-12 rounded-full bg-stone-100" />
        <div className="h-3 w-10 rounded bg-stone-100" />
      </div>
      <div className="h-4 w-3/4 rounded bg-stone-100" />
      <div className="h-3 w-1/2 rounded bg-stone-100 mt-2" />
      <div className="h-3 w-2/3 rounded bg-stone-100 mt-2" />
      <div className="h-4 w-20 rounded-full bg-stone-100 mt-2" />
      <div className="flex items-center justify-between mt-4 pt-3 border-t border-stone-100">
        <div className="h-6 w-16 rounded bg-stone-100" />
        <div className="h-8 w-16 rounded-lg bg-stone-100" />
      </div>
    </div>
  )
}

function BookingSkeletonCard() {
  return (
    <div className="rounded-2xl border border-stone-100 bg-white overflow-hidden animate-pulse">
      <div className="h-1 bg-stone-100" />
      <div className="p-4 sm:p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-2">
            <div className="h-3 w-24 rounded bg-stone-100" />
            <div className="h-5 w-40 rounded bg-stone-100" />
            <div className="h-3 w-28 rounded bg-stone-100" />
          </div>
          <div className="h-6 w-20 rounded-full bg-stone-100" />
        </div>
        <div className="grid grid-cols-2 gap-3 mt-4 pt-4 border-t border-stone-100">
          {Array.from({ length: 4 }, (_, i) => (
            <div key={i} className="space-y-1.5">
              <div className="h-2.5 w-12 rounded bg-stone-100" />
              <div className="h-4 w-20 rounded bg-stone-100" />
            </div>
          ))}
        </div>
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-stone-100">
          <div className="h-6 w-24 rounded bg-stone-100" />
          <div className="h-9 w-24 rounded-lg bg-stone-100" />
        </div>
      </div>
    </div>
  )
}

/** One booking fact — tiny tracked label over the value, the dashboard's idiom. */
function Fact({ label, value }) {
  return (
    <div>
      <p className="text-[8px] font-bold uppercase tracking-[0.18em] text-stone-400">{label}</p>
      <p className="text-xs font-semibold text-astroDark mt-1">{value ?? '—'}</p>
    </div>
  )
}

function BookingCard({ b, astrologerView }) {
  const primary = b.action === 'Join'
  // Full detail lives in the jyotish app — astrologers get the appointment
  // view (host actions), seekers the booking view. Both read /booking-by-id.
  const detailHref = b.id != null
    ? appUrl('jyotish', astrologerView ? `/astrologer/appointments/${b.id}` : `/bookings/${b.id}`)
    : null
  // Seekers can rate a completed consultation once its astrologer id is
  // known — the actual write-review flow (incl. the already-reviewed check)
  // lives on the booking detail page (BookingDetailsPage), not here, so this
  // card only needs to deep-link into it with ?review=1.
  const canRate = !astrologerView && statusType(b) === 'completed' && detailHref && b.astrologerId != null
  const rateHref = canRate ? `${detailHref}?review=1` : null
  return (
    <div className="group relative rounded-2xl border border-amber-800/10 bg-white overflow-hidden shadow-sm transition-all hover:shadow-xl hover:shadow-goldPrimary/10 hover:-translate-y-0.5 flex flex-col">
      {/* Gold hairline — the project's premium accent. */}
      <div className="h-1 bg-gradient-to-r from-goldAccent via-goldPrimary to-goldPrimary/20" />
      <div className="p-5 flex flex-col flex-1">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[9px] font-bold uppercase tracking-[0.18em] text-amber-800/60">Booking {b.code}</p>
            {astrologerView ? (
              <>
                {/* Astrologer view leads with the customer, not the service —
                    the mode already sits in the facts grid below. */}
                <h4 className="text-base font-royal text-astroDark mt-1 leading-snug truncate">{b.client || 'Seeker'}</h4>
                <p className="text-[11px] text-stone-500 mt-0.5 truncate">
                  {[b.clientMobile, b.clientEmail].filter(Boolean).join(' · ') || 'Contact details unavailable'}
                </p>
              </>
            ) : (
              /* Seeker view leads with WHO the session is with — same idiom
                 as the astrologer view; the meet type is just the Mode fact
                 in the grid below. */
              <h4 className="text-base font-royal text-astroDark mt-1 leading-snug truncate">{b.person}</h4>
            )}
          </div>
          <span className={`shrink-0 text-[9px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${b.cls}`}>{b.status}</span>
        </div>

        <div className="grid grid-cols-2 gap-x-3 gap-y-4 mt-4 pt-4 border-t border-stone-100">
          <Fact label="Date" value={b.dateLabel} />
          <Fact label="Time Slot" value={b.slot} />
          <Fact label="Mode" value={b.mode} />
          <Fact label="Duration" value={b.dur} />
        </div>

        <div className="flex items-center justify-between mt-auto pt-4 border-t border-stone-100">
          {b.amount ? (
            <div>
              <span className="num text-lg font-bold text-goldAccent">{b.amount}</span>
              <span className="text-[8px] text-stone-400 block uppercase tracking-wider">Session Fee</span>
            </div>
          ) : <span className="text-[10px] uppercase tracking-wider text-stone-300">✦</span>}
          <div className="flex items-center gap-2">
            {rateHref && (
              <a
                href={rateHref}
                className="text-[9px] font-bold uppercase tracking-wider px-4 py-2.5 rounded-lg border border-amber-800/20 text-amber-800 hover:bg-amber-50 transition-transform hover:-translate-y-0.5"
              >
                ⭐ Rate
              </a>
            )}
            {detailHref ? (
              <a
                href={detailHref}
                className={`text-[9px] font-bold uppercase tracking-wider px-4 py-2.5 rounded-lg transition-transform hover:-translate-y-0.5 ${
                  primary
                    ? 'shine-btn bg-gradient-to-r from-goldAccent to-goldPrimary text-astroDark shadow-md shadow-goldPrimary/10'
                    : 'border border-amber-800/20 text-amber-800 hover:bg-amber-50'
                }`}
              >
                View Details
              </a>
            ) : (
              <button
                className={`text-[9px] font-bold uppercase tracking-wider px-4 py-2.5 rounded-lg transition-transform hover:-translate-y-0.5 ${
                  primary
                    ? 'shine-btn bg-gradient-to-r from-goldAccent to-goldPrimary text-astroDark shadow-md shadow-goldPrimary/10'
                    : 'border border-amber-800/20 text-amber-800 hover:bg-amber-50'
                }`}
              >
                {b.action}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default function ConsultationsTab({ data } = {}) {
  const { user } = useAuth()
  // Astrologers host sessions instead of booking them: their consultations
  // come from /astrologer/:id/bookings and the booking section is hidden.
  const astrologerView = isAstrologerUser(user)

  const { astrologers, hasAstrologers, isLoading: astrologersLoading } = useAstrologers(!astrologerView)
  const { bookings, hasBookings, isLoading: bookingsLoading } = useAstrologerBookings()

  const rows = astrologerView ? bookings : (data?.consultations ?? [])
  const rowsLoading = astrologerView ? bookingsLoading : Boolean(data?.isLoading)
  const hasRows = astrologerView ? hasBookings : rows.length > 0

  // Astrologer rail navigation — the cards overflow horizontally, so the
  // header carries prev/next arrows that page the rail by one viewport.
  // Arrow state tracks the live scroll position (and container resizes).
  const railRef = useRef(null)
  const [rail, setRail] = useState({ canPrev: false, canNext: false })
  const updateRail = () => {
    const el = railRef.current
    if (!el) return
    setRail({
      canPrev: el.scrollLeft > 4,
      canNext: el.scrollLeft + el.clientWidth < el.scrollWidth - 4,
    })
  }
  useEffect(() => {
    updateRail()
    window.addEventListener('resize', updateRail)
    return () => window.removeEventListener('resize', updateRail)
  }, [astrologers.length])
  const scrollRail = (dir) => {
    const el = railRef.current
    // One card (w-64 + gap) short of a full viewport, so context carries over.
    if (el) el.scrollBy({ left: dir * Math.max(el.clientWidth - 272, 272), behavior: 'smooth' })
  }

  // Status sub-tabs — each booking is bucketed by its status type, and the
  // header pills carry live counts. 'all' shows everything.
  const [statusTab, setStatusTab] = useState('upcoming')
  const counts = useMemo(() => {
    const c = { all: rows.length, upcoming: 0, completed: 0, cancelled: 0 }
    rows.forEach((r) => { c[statusType(r)] += 1 })
    return c
  }, [rows])
  const visibleRows = statusTab === 'all' ? rows : rows.filter((r) => statusType(r) === statusTab)

  return (
    <div className="space-y-6">

      {/* Book a Session */}
      {!astrologerView && (
        <div className="dash-card p-4 sm:p-6">
          <div className="flex items-end justify-between gap-3 mb-5">
            <div>
              <span className="eyebrow text-amber-700">Book a Session</span>
              <h3 className="text-xl text-astroDark mt-1 font-royal">Available Astrologers</h3>
            </div>
            {!astrologersLoading && hasAstrologers && (
              <div className="flex items-center gap-2 shrink-0">
                <button
                  type="button"
                  onClick={() => scrollRail(-1)}
                  disabled={!rail.canPrev}
                  aria-label="Previous astrologers"
                  className="w-9 h-9 rounded-full border border-amber-800/20 text-amber-800 flex items-center justify-center transition-all hover:bg-amber-50 hover:border-amber-800/40 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                >
                  <FiChevronLeft size={18} />
                </button>
                <button
                  type="button"
                  onClick={() => scrollRail(1)}
                  disabled={!rail.canNext}
                  aria-label="Next astrologers"
                  className="w-9 h-9 rounded-full border border-amber-800/20 text-amber-800 flex items-center justify-center transition-all hover:bg-amber-50 hover:border-amber-800/40 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                >
                  <FiChevronRight size={18} />
                </button>
              </div>
            )}
          </div>
          {astrologersLoading ? (
            <div className="flex gap-4 overflow-x-auto pb-2">
              {Array.from({ length: 4 }, (_, i) => (
                <div key={i} className="w-64 shrink-0"><AstrologerSkeletonCard /></div>
              ))}
            </div>
          ) : !hasAstrologers ? (
            <div className="py-10 text-center">
              <div className="text-4xl mb-3 opacity-40">🔮</div>
              <p className="text-base font-semibold text-astroDark mb-1">No astrologers available right now</p>
              <p className="text-sm text-stone-400">Please check back in a little while.</p>
            </div>
          ) : (
            /* Single row — extra astrologers stay reachable by horizontal
               scroll (header arrows page the rail) instead of growing the
               section taller. */
            <div ref={railRef} onScroll={updateRail} className="flex gap-4 overflow-x-auto pb-2 snap-x snap-mandatory no-scrollbar">
              {astrologers.map((a) => {
                const live = a.status === 'live'
                const bookHref = live && a.id ? appUrl('jyotish', `/astrologers/${a.id}/book`) : null
                return (
                  <div key={a.key} className="browse-card p-4 flex flex-col w-64 shrink-0 snap-start">
                    <div className="flex items-center justify-between mb-3">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-tr from-goldAccent to-goldPrimary flex items-center justify-center text-astroDark font-royal font-bold text-lg overflow-hidden">
                        {a.img
                          ? <img src={a.img} alt={a.name} className="w-full h-full object-cover" />
                          : a.name.charAt(0)}
                      </div>
                      <span className={`inline-flex items-center gap-1 text-[8px] font-bold uppercase ${live ? 'text-green-600' : 'text-amber-600'}`}><span className={`w-1.5 h-1.5 rounded-full ${live ? 'bg-green-500' : 'bg-amber-500'}`} />{live ? 'Live' : 'Busy'}</span>
                    </div>
                    <p className="text-sm font-semibold text-astroDark leading-tight">{a.name}</p>
                    <p className="text-[10px] uppercase tracking-wider text-stone-400 mt-0.5">{a.role}</p>
                    <div className="flex items-center gap-2 mt-2 text-[10px] text-stone-500"><span>⭐ {a.rating}</span><span>·</span><span>{a.exp}</span></div>
                    <span className="self-start mt-2 text-[8px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-amber-50 text-amber-700">{a.tag}</span>
                    <div className="flex items-center justify-between mt-4 pt-3 border-t border-stone-100">
                      <div><span className="num text-base font-bold text-goldAccent">₹{a.price}</span><span className="text-[8px] text-stone-400 block uppercase tracking-wider">per session</span></div>
                      {bookHref ? (
                        <a href={bookHref} className="text-[9px] font-bold uppercase tracking-wider px-3 py-2 rounded-lg bg-gradient-to-r from-goldAccent to-goldPrimary text-astroDark">Book</a>
                      ) : (
                        <button className="text-[9px] font-bold uppercase tracking-wider px-3 py-2 rounded-lg border border-stone-300 text-stone-500">Notify</button>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}

      {/* My Consultations */}
      <div className="dash-card p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
          <h3 className="text-xl text-astroDark font-royal">My Consultations</h3>
          {!astrologerView && (
            <a
              href={appUrl('jyotish', 'consultations')}
              className="shine-btn bg-gradient-to-r from-goldAccent to-goldPrimary text-astroDark text-[10px] font-bold uppercase tracking-wider px-5 py-2.5 rounded-lg self-start sm:self-auto"
            >
              + Book New
            </a>
          )}
        </div>

        {/* Status sub-tabs — segmented pills with live counts per type. */}
        <div className="flex flex-wrap gap-1 bg-stone-100/80 rounded-xl p-1 w-max max-w-full mb-5">
          {STATUS_TABS.map((t) => {
            const active = t.key === statusTab
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setStatusTab(t.key)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-[10px] font-bold uppercase tracking-wider transition-all ${active ? 'bg-[#3D0A0A] text-white shadow-md shadow-[#3D0A0A]/20' : 'text-stone-500 hover:text-astroDark hover:bg-white'
                  }`}
              >
                {t.label}
                <span className={`num text-[9px] px-1.5 py-0.5 rounded-full ${active ? 'bg-[#D4A017] text-[#3D0A0A]' : 'bg-stone-200/80 text-stone-500'}`}>{counts[t.key]}</span>
              </button>
            )
          })}
        </div>

        {!rowsLoading && !hasRows ? (
          <div className="py-10 text-center">
            <div className="text-4xl mb-3 opacity-40">🗓️</div>
            <p className="text-base font-semibold text-astroDark mb-1">
              {astrologerView ? 'No sessions booked yet' : 'No consultations yet'}
            </p>
            <p className="text-sm text-stone-400">
              {astrologerView
                ? 'Sessions seekers book with you will appear here.'
                : 'Book a session with an astrologer to get started.'}
            </p>
          </div>
        ) : !rowsLoading && visibleRows.length === 0 ? (
          <div className="py-10 text-center">
            <div className="text-4xl mb-3 opacity-40">🗓️</div>
            <p className="text-base font-semibold text-astroDark mb-1">
              No {statusTab} {astrologerView ? 'sessions' : 'consultations'}
            </p>
            <p className="text-sm text-stone-400">Nothing under this status yet — check the other tabs.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-4">
            {rowsLoading
              ? Array.from({ length: 4 }, (_, i) => <BookingSkeletonCard key={i} />)
              : visibleRows.map((r) => <BookingCard key={r.key ?? r.svc} b={r} astrologerView={astrologerView} />)}
          </div>
        )}
      </div>
    </div>
  )
}
